import type { PortalAccountType } from "@aam/shared";
import { isPortalAccountType } from "@aam/shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";
const SESSION_KEY = "aam_session_token";
const IMPERSONATOR_SESSION_KEY = "aam_impersonator_token";
const POST_LOGIN_REDIRECT_KEY = "aam_post_login_redirect";

export interface SessionUser {
  id: string;
  email: string;
  organizationId?: string;
  organization?: OrganizationBrand | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  npn?: string | null;
  role?: "ADMIN" | "PPG" | "PROVIDER" | "AGENT";
  providerImo?: string | null;
  isStaff?: boolean;
  canLogin?: boolean;
  /** Login identity row for portal agents; null for staff sessions. */
  portalUserId?: string | null;
  /** Managed-agent contract subject; null until the portal user applies. */
  managedAgentId?: string | null;
  accessScope?: "OWN" | "DOWNLINE" | "ALL";
  canManageDownlines?: boolean;
  /** Null/absent until the agent has seen the first-run portal tour. */
  portalTourCompletedAt?: string | null;
  /** "RING_IQ" agents are dialer-only — no commission, no contracting. */
  accountType?: PortalAccountType | null;
  commissionLevel?: string | null;
  hasPassword?: boolean;
  impersonation?: {
    id: string;
    email: string;
  } | null;
}

export interface OrganizationBrand {
  id: string;
  slug: string;
  name: string;
  brandName: string;
  logoUrl?: string | null;
  primaryColor?: string | null;
  emailFromName?: string | null;
  onboardingFlowKey?: string;
  eligibleCarriers?: string[];
}

export type SessionAgent = SessionUser;

export interface SessionState {
  agent: SessionUser | null;
  loading: boolean;
  sessionToken: string | null;
  oauthError?: string;
}

interface SessionContextValue extends SessionState {
  logout: () => Promise<void>;
  stopImpersonating: () => Promise<void>;
  refresh: () => Promise<SessionUser | null>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function getSessionToken(): string | null {
  return localStorage.getItem(SESSION_KEY);
}

export function setSessionToken(token: string) {
  localStorage.setItem(SESSION_KEY, token);
}

export function clearSessionToken() {
  localStorage.removeItem(SESSION_KEY);
}

function getImpersonatorToken() {
  return sessionStorage.getItem(IMPERSONATOR_SESSION_KEY);
}

export function setImpersonatorToken(token: string) {
  sessionStorage.setItem(IMPERSONATOR_SESSION_KEY, token);
}

function clearImpersonatorToken() {
  sessionStorage.removeItem(IMPERSONATOR_SESSION_KEY);
}

function parseSessionUser(data: Record<string, unknown>): SessionUser {
  return {
    id: String(data.id ?? data.userId ?? ""),
    email: String(data.email ?? ""),
    organizationId: (data.organizationId as string | undefined) ?? undefined,
    organization: (data.organization as OrganizationBrand | null) ?? null,
    firstName: (data.firstName as string | null) ?? null,
    lastName: (data.lastName as string | null) ?? null,
    phone: (data.phone as string | null) ?? null,
    npn: (data.npn as string | null) ?? null,
    role: data.role as SessionUser["role"],
    isStaff: Boolean(data.isStaff),
    canLogin: data.canLogin as boolean | undefined,
    portalUserId: (data.portalUserId as string | null) ?? null,
    managedAgentId: (data.managedAgentId as string | null) ?? null,
    accessScope: data.accessScope as SessionUser["accessScope"],
    canManageDownlines: Boolean(data.canManageDownlines),
    portalTourCompletedAt: (data.portalTourCompletedAt as string | null) ?? null,
    accountType: isPortalAccountType(data.accountType) ? data.accountType : "STANDARD",
    commissionLevel: (data.commissionLevel as string | null) ?? null,
    providerImo: (data.providerImo as string | null) ?? null,
    hasPassword: Boolean(data.hasPassword),
    impersonation: (data.impersonation as SessionUser["impersonation"]) ?? null,
  };
}

function defaultPostLoginPath(user: SessionUser) {
  if (user.isStaff) {
    return "/dashboard/agents";
  }
  if (user.role === "AGENT") return "/my-agent";
  return "/dashboard/agents";
}

function consumePostLoginRedirect(user: SessionUser) {
  const redirect = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  if (redirect?.startsWith("/")) return redirect;
  return defaultPostLoginPath(user);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({
    agent: null,
    loading: true,
    sessionToken: getSessionToken(),
    oauthError: undefined,
  });

  const verify = useCallback(async (token: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) throw new Error("Verification failed");
      const json = await res.json();
      setSessionToken(json.data.sessionToken);
      const user = parseSessionUser(json.data);
      setState({
        agent: user,
        loading: false,
        sessionToken: json.data.sessionToken,
      });
      return user;
    } catch {
      return null;
    }
  }, []);

  const logout = useCallback(async () => {
    const token = getSessionToken();
    if (token) {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    clearSessionToken();
    clearImpersonatorToken();
    setState({ agent: null, loading: false, sessionToken: null });
  }, []);

  const refresh = useCallback(async () => {
    const token = getSessionToken();
    if (!token) return null;
    try {
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await response.json();
      if (!json.success) return null;
      const user = parseSessionUser(json.data);
      setState((current) => ({
        ...current,
        agent: user,
        loading: false,
        sessionToken: token,
      }));
      return user;
    } catch {
      return null;
    }
  }, []);

  const stopImpersonating = useCallback(async () => {
    const impersonatorToken = getImpersonatorToken();
    const currentToken = getSessionToken();
    if (currentToken) {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${currentToken}` },
      }).catch(() => {});
    }
    clearImpersonatorToken();
    if (impersonatorToken) {
      setSessionToken(impersonatorToken);
      window.location.replace("/dashboard/users");
      return;
    }
    clearSessionToken();
    setState({ agent: null, loading: false, sessionToken: null });
    window.location.replace("/login");
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const magicToken = params.get("token");
    const sessionToken = params.get("session");
    const oauthError = params.get("error");

    if (magicToken || sessionToken || oauthError) {
      params.delete("token");
      params.delete("session");
      params.delete("error");
      const query = params.toString();
      window.history.replaceState(
        {},
        document.title,
        `${window.location.pathname}${query ? `?${query}` : ""}`
      );
    }

    if (oauthError) {
      setState((current) => ({ ...current, loading: false, oauthError }));
      return;
    }

    if (magicToken) {
      verify(magicToken).then((user) => {
        if (user) window.location.replace(consumePostLoginRedirect(user));
        else setState((current) => ({ ...current, loading: false }));
      });
      return;
    }

    if (sessionToken) {
      setSessionToken(sessionToken);
      fetch(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      })
        .then((r) => r.json())
        .then((json) => {
          if (json.success) {
            setState({
              agent: parseSessionUser(json.data),
              loading: false,
              sessionToken,
            });
            window.location.replace(consumePostLoginRedirect(json.data));
          } else {
            clearSessionToken();
            setState({ agent: null, loading: false, sessionToken: null });
          }
        })
        .catch(() => {
          clearSessionToken();
          setState({ agent: null, loading: false, sessionToken: null });
        });
      return;
    }

    const storedToken = getSessionToken();
    if (!storedToken) {
      setState({ agent: null, loading: false, sessionToken: null });
      return;
    }

    fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${storedToken}` },
    })
      .then((r) => r.json())
      .then((json) => {
        if (json.success) {
          setState({
            agent: parseSessionUser(json.data),
            loading: false,
            sessionToken: storedToken,
          });
        } else {
          clearSessionToken();
          setState({ agent: null, loading: false, sessionToken: null });
        }
      })
      .catch(() => {
        setState({ agent: null, loading: false, sessionToken: null });
      });
  }, [verify]);

  const value = useMemo(
    () => ({
      ...state,
      logout,
      stopImpersonating,
      refresh,
    }),
    [state, logout, stopImpersonating, refresh]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return context;
}
